import React, { useCallback, useEffect, useState } from "react";
import { Settings as SlickSettings } from "react-slick";
import { BREAKPOINTS } from "web/react/constants";
import environment from "web/script/modules/environment";
import FullscreenGalleryEmo from "./__LEGACY__/product-image-gallery-fullscreen-emo";
import { Image } from "./product-gallery-image";
import { ProductImageGalleryCarouselEmo } from "./product-image-carousel-emo";
import { ProductImageGalleryFullscreen } from "./product-image-gallery-fullscreen";
import { sendAnalyticsEvent } from "./utils";

type ProductImageGalleryProps = {
    images: Image[];
    currentSlideIndex?: number;
    productId: string;
    buyOptionId: string;
    isInStockProductPage?: boolean;
};

export function ProductImageGallery({
    images,
    productId,
    buyOptionId,
    isInStockProductPage = false,
}: ProductImageGalleryProps): React.ReactElement {
    const [fullscreen, setFullscreen] = useState(false);
    const [initialSlideIndex, setInitialSlideIndex] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [seenAll, setSeenAll] = useState(false);

    const isGalleryRedesignEnabled = !!(
        isInStockProductPage && environment.getFeature("beam_web_pdp_gallery_fullscreen_redesign")
    );

    const handleImageClick = useCallback((event, index): void => {
        setInitialSlideIndex(index);
        setCurrentSlideIndex(index);
        setFullscreen(true);
    }, []);

    const triggerSeenAll = useCallback((): void => {
        setSeenAll(true);
        sendAnalyticsEvent("seen_all", false, productId, buyOptionId);
    }, [productId, buyOptionId]);

    const onSlideChange = useCallback(
        (curSlide, nextSlide): void => {
            setCurrentSlideIndex(nextSlide);
            sendAnalyticsEvent("changed_image", true, productId, buyOptionId);

            if (!seenAll) {
                const lastSlideIndex = images.length - 1;

                if (nextSlide === lastSlideIndex) {
                    triggerSeenAll();
                }
            }
        },
        [images, seenAll, triggerSeenAll, productId, buyOptionId]
    );

    const afterSlideChange = useCallback(
        (currentSlide: number): void => {
            onSlideChange(undefined, currentSlide);
        },
        [onSlideChange]
    );

    useEffect(() => {
        if (!seenAll && images.length === 1 && currentSlideIndex === 0) {
            triggerSeenAll();
        }
    }, [images, currentSlideIndex, buyOptionId, productId, seenAll, triggerSeenAll]);

    const InPageViewSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: currentSlideIndex,
        afterChange: afterSlideChange,
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    slidesToShow: 1,
                    speed: 200, // we need this for smooth swipe
                    touchMove: true,
                    draggable: false,
                },
            },
            {
                breakpoint: BREAKPOINTS.Large,
                settings: {
                    slidesToShow: 1,
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    return (
        <>
            {fullscreen && isGalleryRedesignEnabled ? (
                <ProductImageGalleryFullscreen
                    images={images}
                    initialSlideIndex={initialSlideIndex}
                    onClose={(): void => {
                        setFullscreen(false);
                    }}
                    productId={productId}
                    buyOptionId={buyOptionId}
                />
            ) : fullscreen ? (
                <FullscreenGalleryEmo
                    currentSlideIndex={currentSlideIndex}
                    productId={productId}
                    buyOptionId={buyOptionId}
                    isOpen={fullscreen}
                    onClose={(): void => {
                        setFullscreen(false);
                        sendAnalyticsEvent("close_fullscreen", true, productId, buyOptionId);
                    }}
                    images={images}
                    initialSlideIndex={initialSlideIndex}
                    onSlideChange={onSlideChange}
                    isInStockProductPage={isInStockProductPage}
                />
            ) : (
                <ProductImageGalleryCarouselEmo
                    images={images}
                    settings={InPageViewSettings}
                    isFullscreen={fullscreen}
                    handleImageClick={(event, index): void => handleImageClick(event, index)}
                    onClose={(): void => setFullscreen(false)}
                    currentSlideIndex={currentSlideIndex}
                    isInStockProductPage={isInStockProductPage}
                />
            )}
        </>
    );
}
